import { isIphone } from "../utils.js";
import {
  destroyPhotoSphereViewer,
  initPhotoSphereViewer,
} from "../photo-sphere-viewer.js";
import { RESOLUTION_30MP } from "../consts.js";
import Layer, { EASE_OUT_QUINT } from "./Layer.js";

export default class InteriorLayer extends Layer {
  /**
   * @param {Spinner} spinner
   * @param {HTMLElement} node
   */
  constructor(spinner, node) {
    super(spinner, node);

    this.viewer = null;
    this.image = null;
    this.imageUrl = null;
    this.imageWidth = null;
    this.imageMaxResolution = RESOLUTION_30MP;
    this.imageResolutionExtension = `.${RESOLUTION_30MP}`;
    this.isImageTiled = false;
  }

  /**
   * @param {{
   *   image: string,
   *   imageUrl?: string,
   *   imageWidth?: number,
   *   imageMaxResolution?: number,
   *   isImageTiled?: boolean,
   * }} options
   */
  configure(options) {
    this.image = options.image;
    this.imageWidth = options.imageWidth;
    this.imageMaxResolution = options.imageMaxResolution ?? RESOLUTION_30MP;
    this.imageResolutionExtension =
      this.imageMaxResolution > RESOLUTION_30MP ? "" : `.${RESOLUTION_30MP}`;
    this.imageUrl =
      options.imageUrl ??
      `${this.imagesBaseUrl}/${this.image}${this.imageResolutionExtension}`;
    this.isImageTiled = Boolean(options.isImageTiled);

    // Disable non-tiled XPhase images on iPhones
    if (
      isIphone() &&
      this.imageMaxResolution > RESOLUTION_30MP &&
      !this.isImageTiled
    ) {
      this.imageMaxResolution = RESOLUTION_30MP;
      this.imageResolutionExtension = `.${RESOLUTION_30MP}`;
      this.imageUrl = `${this.imagesBaseUrl}/${this.image}${this.imageResolutionExtension}`;
    }
  }

  createLayerData() {
    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      type: "interior",
      columns: 1,
      rows: 1,
      default_column: 1,
      default_row: 1,
      images: {
        "1-1": {
          id: 1,
          hash: this.image,
          url: this.imageUrl,
        },
      },
    };
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  deactivate() {
    this.host.style.pointerEvents = "none";
    this.host.style.opacity = "0";
  }

  get enabled() {
    return this.opacity === "1";
  }

  set enabled(value) {
    if (!value) {
      if (!this.viewer) {
        return;
      }

      this.fadeOutViewer();
      return;
    }

    if (this.viewer) {
      this.fadeInViewer();
      return;
    }

    this.createViewer();
  }

  fadeInViewer() {
    this.animateNode(
      this.host,
      [{ opacity: 1 }],
      {
        easing: EASE_OUT_QUINT,
        duration: 500,
        override: true,
      },
      () => {
        this.spinner.loaderLayer.visibility = false;
        this.viewer.resize();
        this.spinner.uiControlsEnabled = true;
        this.host.style.pointerEvents = null;
      },
    );
  }

  fadeOutViewer() {
    this.animateNode(
      this.host,
      [{ opacity: 0 }],
      {
        duration: 500,
        easing: EASE_OUT_QUINT,
        override: true,
      },
      () => {
        this.host.style.pointerEvents = "none";
        void this.viewer.stopAnimation();
      },
    );
  }

  createViewer() {
    /*
     * A customer overrides the native Request object, which causes
     * the interior to break. This is a workaround for this scenario.
     */
    const customerRequestClass = window.Request;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);

    const nativeRequestClass = iframe.contentWindow.Request;
    document.body.removeChild(iframe);
    window.Request = nativeRequestClass;

    this.spinner.loaderLayer.visibility = true;

    initPhotoSphereViewer(this.createViewerConfig())
      .then((viewer) => {
        this.viewer = viewer;
        this.viewer.addEventListener("ready", () => {
          requestAnimationFrame(() => {
            this.fadeInViewer();
          });

          window.Request = customerRequestClass;
        });
      })
      .catch((error) => {
        console.error(error);
        window.Request = customerRequestClass;
      });
  }

  createViewerConfig() {
    const config = {
      container: this.subject,
      defaultZoomLvl: 50,
      mousewheel: true,
      minFov: 75,
      maxFov: 75,
      keyboard: false,
      navbar: false,
      panorama: this.imageUrl,
    };

    if (this.imageMaxResolution > RESOLUTION_30MP) {
      config.defaultZoomLvl = 0;
      config.minFov = 30;
      config.maxFov = 90;
    }

    if (this.isImageTiled) {
      const interiorImageUrl = `${this.imagesBaseUrl}/${this.image}`;
      config.adapter = "EquirectangularTilesAdapter";
      config.panorama = {
        width: this.imageWidth,
        cols: 8,
        rows: 8,
        baseUrl: `${interiorImageUrl}.${RESOLUTION_30MP}`,
        tileUrl: (col, row) => {
          return `${interiorImageUrl}.tile.${col}.${row} `;
        },
      };
    }

    return config;
  }

  destroyViewer() {
    destroyPhotoSphereViewer();

    if (!this.viewer) {
      return;
    }

    this.viewer.destroy();
    this.viewer = null;
  }
}
